// <s-delivery-estimate> turns a business-day window into a delivery date range.
// It skips non-working weekdays, public holidays and store closure dates, and re-runs
// whenever the product-form partial re-renders it after a variant change.
class DeliveryEstimate extends HTMLElement {
  connectedCallback() {
    const output = this.querySelector('[data-delivery-range]');
    if (!output) return;

    const minDays = Math.max(0, parseInt(this.dataset.min, 10) || 0);
    const maxDays = Math.max(minDays, parseInt(this.dataset.max, 10) || minDays);
    const workdays = new Set(
      (this.dataset.workdays || '1,2,3,4,5')
        .split(',')
        .map((day) => parseInt(day, 10))
        .filter((day) => !Number.isNaN(day))
    );
    const blocked = new Set(
      `${this.dataset.holidays || ''}\n${this.dataset.closures || ''}`
        .split(/[\s,]+/)
        .map((value) => value.trim())
        .filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))
    );

    const isWorkingDay = (date) => workdays.has(date.getDay()) && !blocked.has(toKey(date));

    const start = startOfDay(new Date());
    const first = addBusinessDays(start, minDays, isWorkingDay);
    const last = addBusinessDays(start, maxDays, isWorkingDay);

    const locale = this.dataset.locale || document.documentElement.lang || undefined;
    const options =
      this.dataset.format === 'short'
        ? { day: 'numeric', month: 'short' }
        : { weekday: 'short', day: 'numeric', month: 'long' };
    const formatter = new Intl.DateTimeFormat(locale, options);

    output.textContent =
      toKey(first) === toKey(last)
        ? formatter.format(first)
        : `${formatter.format(first)} – ${formatter.format(last)}`;
    this.removeAttribute('data-pending');
  }
}

function startOfDay(date) {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function toKey(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function addBusinessDays(start, count, isWorkingDay) {
  const date = new Date(start);
  let added = 0;
  // Guard against a configuration with no working days at all.
  let safety = 0;
  while (added < count && safety < 1000) {
    date.setDate(date.getDate() + 1);
    safety += 1;
    if (isWorkingDay(date)) added += 1;
  }
  // A zero-day window that lands on a closed day rolls forward to the next working day.
  while (!isWorkingDay(date) && safety < 1000) {
    date.setDate(date.getDate() + 1);
    safety += 1;
  }
  return date;
}

if (!customElements.get('s-delivery-estimate')) {
  customElements.define('s-delivery-estimate', DeliveryEstimate);
}
